// JiraOps User Guide — step configuration.
//
// PHASE 3: full walkthroughs for every module — Dashboard (11), Lifecycle (6),
// Workload (10). Each module's last step hands off to the next via TOUR_MODULES;
// the last module simply finishes. The overlay is fully reusable and picks up
// whichever module's steps the active tour points at.
//
// target       CSS selector for a stable, existing anchor (section id / data-tour attr)
// title        one line
// body         1–2 short sentences
// placement    preferred callout side: "top" | "bottom" (a hint; the overlay
//              flips / falls back to keep the callout on screen)
// tall         target is a large / full-width section (informational only now —
//              the overlay always lights the element's full bounding rect)

export const DASHBOARD_STEPS = [
  {
    id: "hero",
    target: "#ov-overview",
    title: "Your engineering command center",
    body:
      "The central view of engineering delivery. It brings together delivery status, operational risk, and execution priorities so you can see what needs attention first.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "viewing-as",
    target: '[data-tour="viewing-as"]',
    title: "Choose who you're viewing",
    body:
      "Keep “All Developers” for the team-wide view, or pick a developer to personalize the relevant sections around their tickets and execution data. Team-wide sections always stay team-wide.",
    placement: "bottom",
  },
  {
    id: "snapshot",
    target: '[data-tour="developer-snapshot"]',
    title: "Your workload at a glance",
    body:
      "How much work exists and where it stands — status mix, which priorities dominate, and the types of work involved. Use the Lifecycle view when you need ticket-level detail.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "action-center",
    target: '[data-tour="action-center"]',
    title: "What needs attention now",
    body:
      "Critical Issues are urgent, high-risk work. Ageing Work has stayed open too long. Quick Wins are low-effort tickets that move progress. Start here each day.",
    placement: "bottom",
  },
  {
    id: "priority-workspace",
    target: '[data-tour="priority-workspace"]',
    title: "Your prioritized work queue",
    body:
      "Every open ticket ranked by impact, so you know what to tackle first. Open any ticket to investigate its delivery risk and details.",
    placement: "top",
    tall: true,
  },
  {
    id: "daily-execution",
    target: "#ov-daily",
    title: "Commitments vs. what shipped",
    body:
      "Each morning developers commit to specific tickets, then close the loop in the evening. GitHub badges show whether committed work actually has PRs behind it.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "threats",
    target: "#ov-threats",
    title: "Threats to delivery",
    body:
      "Workflow friction and PR bottlenecks that put delivery at risk — status regressions, stalled reviews, and stuck tickets. Click any ticket to investigate.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "team-benchmarking",
    target: "#ov-team",
    title: "How you compare to the team",
    body:
      "A selected developer's ticket load and delivery stats, ranked against everyone else. The rest of the benchmarking table always stays team-wide.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "early-warning",
    target: "#ov-early-warning",
    title: "Problems before they become blockers",
    body:
      "Four forward-looking checks on live ticket and PR data. Expand any signal to see the tickets it affects.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "sprint-execution",
    target: "#ov-sprint",
    title: "Sprint execution health",
    body:
      "A live score for the current sprint, built from ticket risk and quick-win availability. The driver cards break down what's moving the number.",
    placement: "top",
    tall: true,
  },
  {
    id: "assistant",
    target: "#ov-assistant",
    title: "Ask the Sprint Assistant",
    body:
      "A place to ask questions about sprint health, team load, and delivery risk in plain language. AI responses are coming soon.",
    placement: "top",
    tall: true,
  },
];

export const LIFECYCLE_STEPS = [
  {
    id: "lc-overview",
    target: "#lc-overview",
    title: "How work moves through the workflow",
    body:
      "Lifecycle Intelligence follows every ticket from creation to resolution — where it sat, who held it, and how long each stage took.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "lc-explorer",
    target: "#lc-explorer",
    title: "Find and inspect any ticket",
    body:
      "Search and filter across every lifecycle stage, then expand a row for its full status history, ownership timeline, and linked PRs. Export the current view as a PDF.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "lc-distribution",
    target: "#lc-distribution",
    title: "Where work is piling up",
    body:
      "Every Jira status grouped into Backlog, In Progress, Review/QA, or Done. A stage holding most of the work in progress is flagged as a bottleneck.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "lc-outliers",
    target: "#lc-outliers",
    title: "Tickets behaving badly",
    body:
      "The slowest resolutions, the most-bounced tickets, and the longest single-stage stalls — the cases worth a closer look.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "lc-recently-closed",
    target: "#lc-recently-closed",
    title: "What just shipped",
    body:
      "The most recently resolved tickets with their total cycle time, so you can see how fast work is actually closing.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "lc-assistant",
    target: "#lc-assistant",
    title: "Ask about lifecycle patterns",
    body:
      "A place to ask about cycle times, bottlenecks, and resolution trends in plain language. AI responses are coming soon.",
    placement: "top",
    tall: true,
  },
];

export const WORKLOAD_STEPS = [
  {
    id: "ws-command",
    target: '[data-tour="ws-command"]',
    title: "Build the report you need",
    body:
      "Pick a developer, a time window, and a preset — then Regenerate. Every section below reflects that scope.",
    placement: "bottom",
  },
  {
    id: "ws-executive",
    target: "#ws-executive",
    title: "The headline",
    body:
      "A plain-language summary of the selected developer's workload, risk, and delivery outlook for the chosen period.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "ws-situation",
    target: "#ws-situation",
    title: "Situation overview",
    body:
      "The core counts — open, closed, critical, and high-priority work — for the current scope.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "ws-period",
    target: "#ws-period",
    title: "Trend vs. the last period",
    body:
      "How the key numbers moved against the previous equivalent window, so improvement or slippage is obvious.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "ws-focus",
    target: "#ws-focus",
    title: "The five that matter now",
    body:
      "The most urgent tickets in scope, with the reason each one is flagged — the same logic behind the recommendations lower down.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "ws-risk",
    target: "#ws-risk",
    title: "Four independent risk buckets",
    body:
      "Critical, ageing, idle, and unassigned work. A ticket can land in more than one — this is where to look before things slip.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "ws-actions",
    target: "#ws-actions",
    title: "What to do next",
    body:
      "Each recommendation comes from a fixed rule against live ticket data, with the reason shown beneath it. Not AI-written.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "ws-capacity",
    target: "#ws-capacity",
    title: "Will the backlog clear?",
    body:
      "Projected time to clear the open backlog at historical versus recent pace — a quick read on whether capacity matches demand.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "ws-commitment-analytics",
    target: "#ws-commitment-analytics",
    title: "Commitment reliability",
    body:
      "How often committed tickets actually get done, and whether committed work has real PR activity behind it.",
    placement: "bottom",
    tall: true,
  },
  {
    id: "ws-ageing-analysis",
    target: "#ws-ageing-analysis",
    title: "Where time is being lost",
    body:
      "Open tickets bucketed by age, so the oldest work — the most likely to stall for good — stays visible.",
    placement: "top",
    tall: true,
  },
];

// Module registry — the overlay reads steps, sticky offset, and hand-off from here.
// route is where the hand-off navigates; stickyOffset is the pinned header stack
// height on that screen (used only for scroll + callout math).
export const TOUR_MODULES = {
  dashboard: {
    key: "dashboard",
    label: "Dashboard",
    route: "/",
    steps: DASHBOARD_STEPS,
    stickyOffset: 108, // TopNav 56 + SectionNav 52
    handoff: { module: "lifecycle", route: "/lifecycle", label: "Go to Lifecycle →" },
  },
  lifecycle: {
    key: "lifecycle",
    label: "Lifecycle",
    route: "/lifecycle",
    steps: LIFECYCLE_STEPS,
    stickyOffset: 52, // SectionNav only
    handoff: { module: "workload", route: "/developer-workload", label: "Go to Workload →" },
  },
  workload: {
    key: "workload",
    label: "Workload",
    route: "/developer-workload",
    steps: WORKLOAD_STEPS,
    stickyOffset: 120, // CommandBar + SectionNav stack (matches ws- scroll-margin)
    handoff: null, // end of the walkthrough
  },
};
